import styled from 'styled-components';
import type { DashboardActivityAlertLevel } from '../../interfaces/dashboardLeaderboardActivity';

export const ActivityTableCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #d8dde3;
  padding: 16px 20px 12px;
  box-shadow: 0 2px 8px rgba(15, 35, 52, 0.08);
  color: #5d6e81;
  font-family: 'Proxima Nova';
  font-size: 12px;
  text-color: #5d6e81;
  overflow: visible;
  p {
    font-family: 'Proxima Nova';
    font-size: 12px;
    color: #000000;
  }
  p strong {
    font-family: 'Proxima Nova Bold';
    font-weight: 600;
    font-size: 30px;
    color: #000000;
    margin-right: 4px;
  }
`;

export const ActivityTableHeader = styled.div`
  position: relative;
  padding: 10px 0 12px;
  border-bottom: 1px solid #e8ecf0;
`;

/** Title + info icon inline, sticky to the word */
export const ActivityTableTitleRow = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  width: 100%;
`;

export const ActivityTableTitle = styled.h2`
  margin: 0;
  padding: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
  color: #000000;
  font-family: 'Proxima Nova Bold', sans-serif;
`;

export const ActivityInfoButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid #000000;
  color: #000000;
  font-size: 10px;
  font-weight: 700;
  font-style: normal;
  line-height: 1;
  cursor: default;
  flex-shrink: 0;
  box-sizing: border-box;
  font-family: 'Proxima Nova Bold', sans-serif;
  transform: translateY(0.5px);
`;


export const ActivityTableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const ActivityTableEl = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 400;
  color: #000000;
  min-width: 1200px;
`;

export const ActivityThGroup = styled.th<{ $tone: 'blue' | 'orange' | 'pink' | 'green' }>`
  padding: 10px 8px;
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  font-family: 'Proxima Nova ExtraBold', sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #000000;
  border: 1px solid #d8dde3;
  border-bottom: none;
  background: ${({ $tone }) =>
    $tone === 'blue'
      ? '#e5f2ff'
      : $tone === 'orange'
        ? '#fff5e5'
        : $tone === 'pink'
          ? '#fff0f0'
          : '#e9fbeb'};
`;

/** Fixed-width slot for dual-chevron sort control. */
export const ActivityThSortIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  min-width: 16px;
  vertical-align: middle;
  flex-shrink: 0;

  .activity-sort-glyphs {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
    gap: 0;
  }

  .activity-sort-arrow {
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 10px;
    cursor: pointer;
    color: #7a8a9e;
    opacity: 0.4;
    transition: color 0.12s ease, opacity 0.12s ease;
  }

  .activity-sort-arrow.active {
    color: #0d5aa7;
    opacity: 1;
  }

  .activity-sort-arrow:focus-visible {
    outline: 1px solid #0d5aa7;
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

export const ActivityThCorner = styled.th`
  padding: 8px 6px;
  border: 1px solid #d8dde3;
  border-bottom: none;
  background: #f5f7f9;
`;

/** Spans both header rows — Player name + sort icon on one line */
export const ActivityThPlayerRowspan = styled.th`
  padding: 12px 10px;
  vertical-align: middle;
  font-weight: 400;
  font-family: 'Proxima Nova Bold', sans-serif;
  font-size: 12px;
  line-height: 1;
  border: 1px solid #d8dde3;
  background: #ffffff;
  color: #000000;
  min-width: 118px;
  width: 118px;
  white-space: nowrap;
  cursor: pointer;

  & > .activity-th-player-inner {
    display: inline-flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    white-space: nowrap;
    width: 100%;
  }

  .activity-th-sort-content {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 6px;
    padding: 0 6px;
    box-sizing: border-box;
  }

  & .activity-th-player-inner svg {
    margin-left: 0;
  }

  &:hover .activity-sort-title {
    color: #0d5aa7;
  }

  &[aria-sort='ascending'] .activity-sort-title,
  &[aria-sort='descending'] .activity-sort-title {
    color: #0d5aa7;
  }
`;

export const ActivityTh = styled.th`
  padding: 10px 8px;
  text-align: center;
  font-weight: 400;
  font-family: 'Proxima Nova Semibold', sans-serif;
  font-size: 12px;
  white-space: nowrap;
  border: 1px solid #d8dde3;
  background: #f5f7f9;
  color: #000000;
`;

export const ActivityThSortable = styled(ActivityTh)`
  cursor: default;
`;

/** Daily check in — Date + Arm / Body / Sleep */
export const ActivityThDaily = styled.th`
  padding: 10px 8px;
  text-align: center;
  font-weight: 400;
  font-family: 'Proxima Nova Semibold', sans-serif;
  font-size: 12px;
  white-space: nowrap;
  border: 1px solid #d8dde3;
  background: #e5f2ff;
  color: #000000;
`;

export const ActivityThDailySortable = styled(ActivityThDaily)`
  cursor: pointer;
  font-family: 'Proxima Nova Bold', sans-serif;


  .activity-th-sort-content {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 6px;
    padding-left: 8px;
    box-sizing: border-box;
    vertical-align: middle;
  }

  &.activity-th-left-title {
    text-align: left;
  }

  &.activity-th-left-title .activity-th-sort-content {
    justify-content: flex-start;
  }

  &.activity-th-left-title .activity-sort-title {
    display: inline-block;
    line-height: 1.1;
  }

  &:hover .activity-sort-title {
    color: #0d5aa7;
  }

  &[aria-sort='ascending'] .activity-sort-title,
  &[aria-sort='descending'] .activity-sort-title {
    color: #0d5aa7;
  }
`;

/** Fresh alerts — Date, Strength, Recovery */
export const ActivityThFresh = styled.th`
  padding: 10px 8px;
  text-align: center;
  font-weight: 400;
  font-family: 'Proxima Nova Semibold', sans-serif;
  font-size: 12px;
  white-space: nowrap;
  border: 1px solid #d8dde3;
  background: #fff5e5;
  color: #000000;
`;

export const ActivityThFreshSortable = styled(ActivityThFresh)`
  cursor: pointer;
  font-family: 'Proxima Nova Bold', sans-serif;

  .activity-th-sort-content {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 6px;
    padding: 0 6px 0 10px;
    box-sizing: border-box;
    vertical-align: middle;
  }

  &:hover .activity-sort-title {
    color: #0d5aa7;
  }

  &[aria-sort='ascending'] .activity-sort-title,
  &[aria-sort='descending'] .activity-sort-title {
    color: #0d5aa7;
  }
`;

/** Post alerts — Date (last post), Fatigue, Type, RPE, Count */
export const ActivityThPost = styled.th`
  padding: 10px 8px;
  text-align: center;
  font-weight: 400;
  font-family: 'Proxima Nova Semibold', sans-serif;
  font-size: 12px;
  white-space: nowrap;
  border: 1px solid #d8dde3;
  background: #fff0f0;
  color: #000000;
`;

export const ActivityThPostSortable = styled(ActivityThPost)`
  cursor: pointer;
  font-family: 'Proxima Nova Bold', sans-serif;

  .activity-th-sort-content {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 6px;
    padding: 0 6px 0 10px;
    box-sizing: border-box;
    vertical-align: middle;
  }

  &:hover .activity-sort-title {
    color: #0d5aa7;
  }

  &[aria-sort='ascending'] .activity-sort-title,
  &[aria-sort='descending'] .activity-sort-title {
    color: #0d5aa7;
  }
`;

/** Training — Sessions */
export const ActivityThTraining = styled.th`
  padding: 10px 8px;
  text-align: center;
  font-weight: 400;
  font-family: 'Proxima Nova Semibold', sans-serif;
  font-size: 12px;
  white-space: nowrap;
  border: 1px solid #d8dde3;
  background: #e9fbeb;
  color: #000000;
`;

export const ActivityThTrainingSortable = styled(ActivityThTraining)`
  cursor: pointer;
  font-family: 'Proxima Nova Bold', sans-serif;

  .activity-th-sort-content {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 6px;
    padding: 0 6px 0 10px;
    box-sizing: border-box;
    vertical-align: middle;
  }

  &:hover .activity-sort-title {
    color: #0d5aa7;
  }

  &[aria-sort='ascending'] .activity-sort-title,
  &[aria-sort='descending'] .activity-sort-title {
    color: #0d5aa7;
  }
`;

export const ActivityTd = styled.td`
  padding: 10px 8px;
  text-align: center;
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 400;
  font-size: 12px;
  border: 1px solid #e8ecf0;
  vertical-align: middle;
  background: #fff;
`;

export const ActivityTdDaily = styled(ActivityTd)`
  background: #f5faff;
`;

/** Fresh data cells — #FFF5E5 at ~38% alpha */
export const ActivityTdFresh = styled(ActivityTd)`
  background: #fff5e561;
`;

export const ActivityTdPost = styled(ActivityTd)`
  background: #fffafa;
`;

export const ActivityTdTraining = styled(ActivityTd)`
  background: #f6fef7;
`;

export const ActivityTdPlayer = styled(ActivityTd)`
  text-align: left;
  font-weight: 400;
  font-family: 'Proxima Nova', sans-serif;
  color: #000000;
  white-space: nowrap;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Link-style control inside the player name cell (opens player Key Metrics). */
export const ActivityPlayerNameButton = styled.button`
  all: unset;
  display: block;
  width: 100%;
  max-width: 100%;
  cursor: pointer;
  box-sizing: border-box;
  font: inherit;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Proxima Nova Bold', sans-serif;

  &:hover {
    color: #094580;
  }
  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
  &:focus-visible {
    outline: 2px solid #0d5aa7;
    outline-offset: 2px;
  }
`;

/** Summary + pagination below the card (outside border) */
export const ActivityTableBelowCard = styled.div`
  margin-top: 12px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
`;

export const ActivityFooterSummary = styled.p`
  margin: 0;
  font-size: 12px;
  color: #5d6e81;
  font-family: 'Proxima Nova', sans-serif;
`;

const pillBg = (level: DashboardActivityAlertLevel): string => {
  if (level == null) return '#f0f4f8';
  switch (level) {
    case 'no alerts':
      return '#d6ebff';
    case 'watch':
      return '#ffe0b8';
    case 'warning':
      return '#e8d4f0';
    case 'medical':
      return '#d4f0dc';
    default:
      return '#f0f4f8';
  }
};

const pillColor = (level: DashboardActivityAlertLevel): string => {
  if (level == null) return '#5d6e81';
  switch (level) {
    case 'no alerts':
      return '#0d5aa7';
    case 'watch':
      return '#b35c00';
    case 'warning':
      return '#5c2d7a';
    case 'medical':
      return '#1d6b3a';
    default:
      return '#5d6e81';
  }
};

export const ActivityAlertPill = styled.span<{ $level: DashboardActivityAlertLevel }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 400;
  font-family: 'Proxima Nova Bold', sans-serif;
  text-transform: capitalize;
  white-space: nowrap;
  background: ${({ $level }) => pillBg($level)};
  color: ${({ $level }) => pillColor($level)};

  &::before {
    content: '•';
    font-size: 14px;
    line-height: 1;
  }
`;
